import React, { useState, useEffect, useMemo, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Copyright } from '@/components/molecules/copyright'
import { useHistory, useLocation } from 'react-router-dom'
import logo from '@/static/images/icon/logo.svg'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { pmsAuthMfaChallenge, pmsAuthMfaVerify } from '@/apis/aipass'
import { AxiosError } from 'axios'
import { OtpInput } from '@/components/organisms/signin/otp-input'
import { OtpMailHelpPopup } from '@/components/organisms/signin/otp-mail-help-popup'
import { notification } from 'antd'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { reportError } from '@/components/apps/error-boundary'
import { AuthContext } from '@/contexts/auth'
import { useAppDispatch, useAppSelector } from '@/features/hooks'
import { getMfaErrorMessage, MfaErrorResponse } from '@/features/mfa/mfa-error-message'
import { clearEmployeeSendError, clearHotelSendError, resetMfaOtpSend } from '@/features/mfa/mfa-slice'
import { sendMfaOtpToEmployee } from '@/features/mfa/mfa-thunks'

const OTP_LENGTH = 6

export const VerifyOtp: React.FC<{}> = () => {
  const history = useHistory()
  const location = useLocation<{ idToken: string }>()
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setAuth } = useContext<any>(AuthContext)
  const dispatch = useAppDispatch()
  const employeeSend = useAppSelector(state => state.mfa.employeeSend)

  const [otpValues, setOtpValues] = useState<string[]>(Array.from({ length: OTP_LENGTH }, () => ''))
  const [formError, setFormError] = useState<string | undefined>()
  const [isVerifying, setIsVerifying] = useState<boolean>(false)
  const [accountEmail, setAccountEmail] = useState<string>()
  const [hotelEmail, setHotelEmail] = useState<string>()
  const [otpSendEmail, setOtpSendEmail] = useState<string>()
  const [isMailHelpPopupOpen, setIsMailHelpPopupOpen] = useState<boolean>(false)
  const [inputFocusTrigger, setInputFocusTrigger] = useState<number>(0)

  const otpCode = useMemo(() => otpValues.join(''), [otpValues])
  const isOtpComplete = useMemo(() => otpCode.length === OTP_LENGTH, [otpCode])
  const isEmployeeSending = employeeSend.status === 'sending'
  // ポップアップは裏の画面を覆うだけで Tab では裏のボタンに届くため、開いている間も裏の操作を受け付けない
  const isOperationLocked = isVerifying || isEmployeeSending || isMailHelpPopupOpen
  // チャレンジの応答前は送信先が決まっておらず、最初の送信とも重なるため、再送の操作を受け付けない
  const isSendDisabled = accountEmail === undefined || isOperationLocked
  const isVerifyDisabled = isOperationLocked || !isOtpComplete
  const displayedError = formError ?? employeeSend.error

  // 表示時に 1 回だけチャレンジと最初の送信を行う
  useEffect(() => {
    if (!location.state?.idToken) {
      history.replace({ pathname: '/' })
      return
    }
    // 応答前に画面を離れたときは、メールを送ったり画面を切り替えたりしない
    let isMounted = true
    pmsAuthMfaChallenge(location.state.idToken)
      .then(res => {
        if (!isMounted) {
          return
        }
        if (!res.mfaRequired) {
          setAuth(location.state)
          history.replace({ pathname: '/dashboard' })
          return
        }
        setAccountEmail(res.email)
        setHotelEmail(res.hotelEmail)
        setOtpSendEmail(res.email)
        dispatch(sendMfaOtpToEmployee(location.state.idToken))
      })
      .catch(e => {
        reportError(e)
        if (isMounted) {
          errorHandler(e)
        }
      })
    return () => {
      isMounted = false
    }
  }, [])

  // 表示時に戻すと最初の描画で前回の状態を読んでしまうため、画面を離れるときに戻す
  useEffect(() => {
    return () => {
      dispatch(resetMfaOtpSend())
    }
  }, [])

  const onVerify = async () => {
    if (!isOtpComplete) {
      setFormError('認証コードを6桁入力してください。')
      return
    }

    setIsVerifying(true)
    try {
      await pmsAuthMfaVerify(location.state.idToken, { otp: otpValues.join('') })
      setAuth(location.state)
    } catch (e) {
      const error = e as AxiosError<MfaErrorResponse>
      setIsVerifying(false)
      setFormError(getMfaErrorMessage(error, 'otp_verification_failed'))
    }
  }

  // 入力画面のエラーは認証のもの（formError）とログインアドレス宛ての送信のもの（store）の 2 つを合わせて表示しているため、まとめて消す
  const clearErrors = () => {
    setFormError(undefined)
    dispatch(clearEmployeeSendError())
  }

  const onOtpSent = (sentEmail?: string) => {
    setOtpSendEmail(sentEmail)
    notification.open({ message: t('メールを送信しました') })
    setOtpValues(Array.from({ length: OTP_LENGTH }, () => ''))
    clearErrors()
    setInputFocusTrigger(prev => prev + 1)
  }

  const onResend = async () => {
    clearErrors()
    const result = await dispatch(sendMfaOtpToEmployee(location.state.idToken))
    if (sendMfaOtpToEmployee.fulfilled.match(result)) {
      onOtpSent(accountEmail)
    }
  }

  const openMailHelpPopup = () => {
    dispatch(clearHotelSendError())
    setIsMailHelpPopupOpen(true)
  }

  const onSentToHotel = () => {
    setIsMailHelpPopupOpen(false)
    onOtpSent(hotelEmail)
  }

  const onLogout = () => {
    history.replace({ pathname: '/' })
  }

  return (
    <>
      <div css={signinContainerStyle}>
        <div css={signinFormContainerStyle}>
          <img alt="aipass logo" src={logo} css={logoStyle} />
          <div css={otpCardStyle}>
            <p css={titleStyle}>{t('メール認証')}</p>
            <p css={emailStyle}>{otpSendEmail}</p>
            <p css={descriptionStyle}>{t('に届いた6桁のコードを入力してください。')}</p>

            <OtpInput
              values={otpValues}
              onValuesChange={values => {
                setOtpValues(values)
                clearErrors()
              }}
              ariaLabelPrefix={t('認証コード')}
              focusTrigger={inputFocusTrigger}
            />

            {displayedError ? <div css={errorTextStyle}>{t(displayedError)}</div> : null}

            <Button
              buttonType={isVerifyDisabled ? ButtonType.PrimaryDisabled : ButtonType.Primary}
              disabled={isVerifyDisabled}
              width={275}
              height={38}
              marginTop={22}
              marginLeft={'auto'}
              marginRight={'auto'}
              onClick={onVerify}
            >
              {t('認証する')}
            </Button>

            <div css={dividerStyle} />

            <button css={subLinkStyle} disabled={isSendDisabled} onClick={onResend}>
              {t('コードを再送する')}
            </button>
            <button css={resentTextStyle} disabled={isSendDisabled} onClick={openMailHelpPopup}>
              {t('メールを受け取れない場合はこちら')}
            </button>
          </div>

          <button css={backLinkStyle} onClick={onLogout}>
            {t('ログイン画面に戻る')}
          </button>
        </div>
        <div css={footerStyle}>
          <Copyright style={{ position: 'relative', marginTop: 0, bottom: 0 }} />
        </div>
      </div>

      {isMailHelpPopupOpen ? (
        <OtpMailHelpPopup
          idToken={location.state.idToken}
          hotelEmail={hotelEmail}
          onSent={onSentToHotel}
          onCancel={() => setIsMailHelpPopupOpen(false)}
        />
      ) : null}
    </>
  )
}

const baseTextStyle = css({
  fontSize: 12,
  color: '#272727',
  lineHeight: 1.5,
})

const signinContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '0 auto',
  backgroundImage: `url(${require('@/static/images/icon/background-pc.png')})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: '100vh',
  padding: '24px 12px 32px',
})

const signinFormContainerStyle = css({
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

const logoStyle = css({
  width: 210,
  maxWidth: '70vw',
  marginBottom: 24,
})

const otpCardStyle = css({
  width: 360,
  maxWidth: '92vw',
  minHeight: 388,
  background: '#FFFFFF',
  borderRadius: '96px 96px 88px 88px',
  padding: '54px 28px 42px',
  textAlign: 'center',
  boxShadow: '0 14px 30px rgba(0, 0, 0, 0.2)',
})

const titleStyle = css(baseTextStyle, {
  fontSize: 18,
  fontWeight: 700,
  margin: 0,
})

const emailStyle = css(baseTextStyle, {
  margin: '14px 0 4px',
  fontWeight: 700,
})

const descriptionStyle = css(baseTextStyle, {
  margin: 0,
  color: '#676767',
})

const errorTextStyle = css(baseTextStyle, {
  marginTop: 14,
  color: '#D34A35',
})

const dividerStyle = css({
  width: '100%',
  height: 1,
  backgroundColor: '#EFF1F6',
  margin: '24px 0 16px',
})

const subLinkStyle = css(baseTextStyle, {
  display: 'block',
  width: '100%',
  border: 'none',
  background: 'transparent',
  color: '#f2a40b',
  fontWeight: 700,
  marginTop: 16,
  cursor: 'pointer',
  padding: 0,
  '&:hover': { opacity: 0.8 },
  '&:disabled': { cursor: 'default', opacity: 0.5 },
})

const resentTextStyle = css(subLinkStyle, {
  color: '#676767',
  fontWeight: 500,
})

const backLinkStyle = css(baseTextStyle, {
  marginTop: 24,
  border: 'none',
  background: 'transparent',
  color: '#676767',
  cursor: 'pointer',
  '&:hover': { opacity: 0.8 },
})

const footerStyle = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
})
